import { useMutation, useQuery, QueryResult, MutationUpdaterFn, ApolloError } from '@apollo/client';
import { UPDATE_NOTIFICATION_SETTINGS, GET_NOTIFICATION_SETTINGS } from './queries';
import { NotificationSettingMode, NotificationType } from '@tendium/prom-types/notifications';
import { NotificationGroupName } from 'src/models/notifications/types';
import { INotificationSettingsGroup } from './types';
import { useTranslation } from 'react-i18next';
import { useCallback, useMemo } from 'react';
import { notification, useMe } from 'src/common';
import { NOTIFICATIONS_SETTINGS_SORT } from './mappers';
import { FeatureFlag, useFeatureFlag } from 'src/helpers/featureFlag';

export function useTenderUpdateVisibility(): boolean {
  const isBidspacesFeature = useFeatureFlag(FeatureFlag.Bidspaces);
  const featureFlags: boolean[] = [
    !!useFeatureFlag(FeatureFlag.TenderUpdatesDocuments),
    !!useFeatureFlag(FeatureFlag.TenderUpdatesDeadline),
    !!useFeatureFlag(FeatureFlag.TenderUpdatesDeadline),
    !!useFeatureFlag(FeatureFlag.TenderUpdatesAdditions),
    !!useFeatureFlag(FeatureFlag.TenderUpdatesAwards),
    !!useFeatureFlag(FeatureFlag.TenderUpdatesCanceledTender),
    !!useFeatureFlag(FeatureFlag.TenderUpdatesQuestionDeadline),
    !!useFeatureFlag(FeatureFlag.TenderUpdatesValidityPeriod),
    !!useFeatureFlag(FeatureFlag.TenderUpdatesQA)
  ];

  return !!isBidspacesFeature && !!featureFlags.find(feature => feature);
}

interface IGetNotificationSettingsResult {
  getMe: {
    account: { notificationSettingGroups: INotificationSettingsGroup[] };
  };
}

export function useGetNotificationSettings(): Omit<QueryResult<IGetNotificationSettingsResult>, 'data'> & {
  data: INotificationSettingsGroup[];
} {
  const result = useQuery<IGetNotificationSettingsResult>(GET_NOTIFICATION_SETTINGS);

  const isTenderUpdateFeature = useTenderUpdateVisibility();
  const isMonitoringProfilesFeature = useFeatureFlag(FeatureFlag.MonitoringProfiles);

  return useMemo(
    () => ({
      ...result,
      data:
        result.data && result.data.getMe.account
          ? [
              ...result.data.getMe.account.notificationSettingGroups.filter(
                group =>
                  !(
                    (!isTenderUpdateFeature && group.name === NotificationGroupName.WS_SENDOUT) ||
                    (!isMonitoringProfilesFeature && group.name === NotificationGroupName.MP_DAILY_SENDOUT)
                  )
              )
            ]
              .sort(
                (a, b) =>
                  NOTIFICATIONS_SETTINGS_SORT.indexOf(NotificationGroupName[a.name]) -
                  NOTIFICATIONS_SETTINGS_SORT.indexOf(NotificationGroupName[b.name])
              )
              .filter(group => group.isActive)
              .map(group => ({
                ...group,
                notificationSettings: group.notificationSettings.filter(setting => setting.isActive)
              }))
          : []
    }),
    [isMonitoringProfilesFeature, isTenderUpdateFeature, result]
  );
}

interface IUpdateNotificationSettingsResponse {
  updateNotificationSettings: {
    email: string;
    notificationSettingGroups: INotificationSettingsGroup[];
  };
}
interface IUpdateNotificationSettingsInput {
  name: keyof typeof NotificationType;
  mode: NotificationSettingMode;
  value: boolean;
  itemId?: string;
}
export function useUpdateNotificationSettings(): [
  (data: IUpdateNotificationSettingsInput) => void,
  { loading: boolean; error: ApolloError | undefined }
] {
  const { t } = useTranslation();
  const { data: meData } = useMe();
  const email = meData?.email;
  const [updateSettings, { loading, error }] = useMutation<
    IUpdateNotificationSettingsResponse,
    IUpdateNotificationSettingsInput
  >(UPDATE_NOTIFICATION_SETTINGS);

  const updateSettingsFn = useCallback(
    (data: IUpdateNotificationSettingsInput) => {
      const { name, mode, value, itemId } = data;

      updateSettings({
        variables: { name, mode, value, itemId },
        update: getUpdateCacheOnUpdateNotificationSettings(email)
      }).catch(() => {
        notification.error({
          description: t('Common.unknownErrorDesc'),
          message: t('Common.unknownError')
        });
      });
    },
    [email, t, updateSettings]
  );
  return [updateSettingsFn, { loading, error }];
}

function getUpdateCacheOnUpdateNotificationSettings(
  email?: string
): MutationUpdaterFn<IUpdateNotificationSettingsResponse> {
  return (cache, { data }) => {
    if (!data) {
      return;
    }
    if (!email) {
      return;
    }
    const userRef = cache.identify({
      __typename: 'User',
      email
    });
    cache.modify({
      id: userRef,
      fields: {
        notificationSettingGroups() {
          return data.updateNotificationSettings.notificationSettingGroups;
        }
      }
    });
  };
}
